import { Link } from 'react-router-dom'
import type { LucideIcon } from 'lucide-react'
import { ArrowRight } from 'lucide-react'
import { motion } from 'motion/react'
import { cn } from '@/lib/utils'
import type { AppCardTheme } from '@/lib/app-card-config'

interface AppCardProps {
  readonly name: string
  readonly description: string
  readonly icon: LucideIcon
  readonly path: string
  readonly badge?: string
  readonly disabled?: boolean
  readonly theme: AppCardTheme
  readonly stat?: string
  readonly index?: number
}

export function AppCard({
  name,
  description,
  icon: Icon,
  path,
  badge,
  disabled,
  theme,
  stat,
  index = 0,
}: AppCardProps) {
  const content = (
    <motion.div
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.4, delay: index * 0.08, ease: [0.25, 0.46, 0.45, 0.94] }}
      whileHover={
        disabled
          ? undefined
          : {
              scale: 1.02,
              boxShadow: theme.glowColor,
              borderLeftColor: theme.accentHex,
            }
      }
      style={{ borderLeftColor: 'transparent' }}
      className={cn(
        'group relative h-full overflow-hidden rounded-xl border border-border/50 bg-card/80 backdrop-blur-[2px] p-5',
        'border-l-[3px] transition-colors duration-200',
        disabled
          ? 'cursor-not-allowed opacity-60'
          : 'cursor-pointer'
      )}
    >
      {/* Gradient orb */}
      <div
        className="pointer-events-none absolute -right-8 -top-8 h-32 w-32 rounded-full blur-2xl opacity-0 transition-opacity duration-500 group-hover:opacity-100"
        style={{ backgroundColor: theme.orbColor }}
      />

      <div className="relative space-y-3">
        <div className="flex items-start justify-between">
          <div
            className={cn(
              'flex h-12 w-12 items-center justify-center rounded-xl',
              theme.iconBgClass
            )}
          >
            <Icon className={cn('h-6 w-6', theme.iconColorClass)} />
          </div>
          <div className="flex items-center gap-2">
            {badge && (
              <span className="rounded-md bg-muted/80 px-2 py-0.5 text-[11px] font-medium text-muted-foreground">
                {badge}
              </span>
            )}
            {stat && (
              <span className="rounded-md bg-muted/60 px-2 py-0.5 text-[11px] font-medium text-muted-foreground">
                {stat}
              </span>
            )}
            {!disabled && (
              <ArrowRight className="h-4 w-4 text-muted-foreground opacity-0 transition-opacity duration-200 group-hover:opacity-100" />
            )}
          </div>
        </div>
        <div className="space-y-1">
          <h3 className="text-base font-semibold tracking-tight">{name}</h3>
          <p className="text-sm leading-relaxed text-muted-foreground line-clamp-2">
            {description}
          </p>
        </div>
      </div>
    </motion.div>
  )

  if (disabled) {
    return content
  }

  return (
    <Link to={path} className="block h-full">
      {content}
    </Link>
  )
}
